import React, { useState } from 'react';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { mockFormSubmission, finalCTA } from '../data/mock';

const ContactForm = ({ formRef }) => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    monthlySaasSpend: '',
    teamSize: '',
    integrationComplexity: '',
    platformType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await mockFormSubmission(formData);
      if (result.success) {
        setIsSuccess(true);
        setFormData({
          name: '',
          company: '',
          email: '',
          monthlySaasSpend: '',
          teamSize: '',
          integrationComplexity: '',
          platformType: '',
          message: ''
        });
        
        // Reset success message after 5 seconds
        setTimeout(() => {
          setIsSuccess(false);
        }, 5000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section ref={formRef} className="py-20 bg-gradient-to-br from-gray-50 to-white" id="contact">
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0E3A5F] mb-4">
              {finalCTA.headline}
            </h2>
            <p className="text-xl text-gray-600 mb-6">
              {finalCTA.description}
            </p>
            <ul className="inline-block text-left space-y-2">
              {finalCTA.points.map((point, index) => (
                <li key={index} className="flex items-center gap-2 text-gray-700">
                  <CheckCircle2 className="h-5 w-5 text-[#7AC943]" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Success Message */}
          {isSuccess && (
            <div className="mb-8 p-6 bg-[#7AC943]/10 border-2 border-[#7AC943] rounded-xl text-center animate-in fade-in slide-in-from-top duration-500">
              <CheckCircle2 className="h-12 w-12 text-[#7AC943] mx-auto mb-3" />
              <h3 className="text-2xl font-bold text-[#0E3A5F] mb-2">Thank You!</h3>
              <p className="text-gray-700">
                We've received your information and will contact you shortly to schedule your free SaaS audit.
              </p>
            </div>
          )}

          {/* Form */}
          <div className="bg-white p-8 md:p-12 rounded-2xl shadow-2xl border-2 border-gray-100">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-[#0E3A5F] mb-2">
                    Name *
                  </label>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="John Doe"
                    className="w-full border-2 border-gray-200 focus:border-[#1F6FB2] rounded-lg"
                  />
                </div>

                {/* Company */}
                <div>
                  <label htmlFor="company" className="block text-sm font-semibold text-[#0E3A5F] mb-2">
                    Company *
                  </label>
                  <Input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    placeholder="Your Company Inc."
                    className="w-full border-2 border-gray-200 focus:border-[#1F6FB2] rounded-lg"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-[#0E3A5F] mb-2">
                    Email *
                  </label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@company.com"
                    className="w-full border-2 border-gray-200 focus:border-[#1F6FB2] rounded-lg"
                  />
                </div>

                {/* Monthly SaaS Spend */}
                <div>
                  <label htmlFor="monthlySaasSpend" className="block text-sm font-semibold text-[#0E3A5F] mb-2">
                    Monthly SaaS Spend *
                  </label>
                  <Select 
                    value={formData.monthlySaasSpend} 
                    onValueChange={(value) => handleSelectChange('monthlySaasSpend', value)}
                    required
                  >
                    <SelectTrigger className="w-full border-2 border-gray-200 focus:border-[#1F6FB2] rounded-lg">
                      <SelectValue placeholder="Select range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5000-10000">$5,000 - $10,000</SelectItem>
                      <SelectItem value="10000-25000">$10,000 - $25,000</SelectItem>
                      <SelectItem value="25000-50000">$25,000 - $50,000</SelectItem>
                      <SelectItem value="50000+">$50,000+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Pre-qualification Questions */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Team Size */}
                <div>
                  <label htmlFor="teamSize" className="block text-sm font-semibold text-[#0E3A5F] mb-2">
                    Team Size Using Software *
                  </label>
                  <Select 
                    value={formData.teamSize} 
                    onValueChange={(value) => handleSelectChange('teamSize', value)}
                    required
                  >
                    <SelectTrigger className="w-full border-2 border-gray-200 focus:border-[#1F6FB2] rounded-lg">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1-10">1-10 users</SelectItem>
                      <SelectItem value="11-50">11-50 users</SelectItem>
                      <SelectItem value="51-200">51-200 users</SelectItem>
                      <SelectItem value="200+">200+ users</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Integration Complexity */}
                <div>
                  <label htmlFor="integrationComplexity" className="block text-sm font-semibold text-[#0E3A5F] mb-2">
                    Integration Needs *
                  </label>
                  <Select 
                    value={formData.integrationComplexity} 
                    onValueChange={(value) => handleSelectChange('integrationComplexity', value)}
                    required
                  >
                    <SelectTrigger className="w-full border-2 border-gray-200 focus:border-[#1F6FB2] rounded-lg">
                      <SelectValue placeholder="Select complexity" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unknown">Not sure / Unknown</SelectItem>
                      <SelectItem value="none">None / Standalone system</SelectItem>
                      <SelectItem value="simple">Simple (1-3 standard APIs)</SelectItem>
                      <SelectItem value="moderate">Moderate (4-8 integrations)</SelectItem>
                      <SelectItem value="complex">Complex (9+ or legacy systems)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Platform Type */}
              <div>
                <label htmlFor="platformType" className="block text-sm font-semibold text-[#0E3A5F] mb-2">
                  Platform Type *
                </label>
                <Select 
                  value={formData.platformType} 
                  onValueChange={(value) => handleSelectChange('platformType', value)}
                  required
                >
                  <SelectTrigger className="w-full border-2 border-gray-200 focus:border-[#1F6FB2] rounded-lg">
                    <SelectValue placeholder="Select platform" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="web-only">Web application only</SelectItem>
                    <SelectItem value="web-mobile">Web + Mobile apps</SelectItem>
                    <SelectItem value="mobile-only">Mobile apps only</SelectItem>
                    <SelectItem value="not-sure">Not sure yet</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-[#0E3A5F] mb-2">
                  Tell us about your needs
                </label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  placeholder="What SaaS tools are you currently using? What challenges are you facing?"
                  className="w-full border-2 border-gray-200 focus:border-[#1F6FB2] rounded-lg resize-none"
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#7AC943] hover:bg-[#6AB833] text-white py-6 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isSubmitting ? 'Submitting...' : finalCTA.ctaButton}
                {!isSubmitting && <ArrowRight className="ml-2 h-5 w-5" />}
              </Button>

              <p className="text-xs text-gray-500 text-center mt-4">
                By submitting this form, you agree to discuss your project scope and receive a candid assessment of timeline feasibility.
              </p>
              
              <div className="text-center mt-6 pt-6 border-t-2 border-gray-100">
                <p className="text-sm text-[#7AC943] font-semibold mb-2">
                  {finalCTA.socialProof}
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
